import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Play, Square, Clock, UserX, Target, AlertTriangle, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MatchControl({ partido: initialPartido, onBack }) {
  const [partido, setPartido] = useState(initialPartido);
  const [eventos, setEventos] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventType, setEventType] = useState('gol');
  const [eventTeam, setEventTeam] = useState('local');
  const [descripcion, setDescripcion] = useState('');
  const [showFinalizarDialog, setShowFinalizarDialog] = useState(false);
  const [comentarioFinal, setComentarioFinal] = useState('');

  useEffect(() => {
    loadPartidoDetail();
  }, []);

  const loadPartidoDetail = async () => {
    try {
      const response = await axios.get(`${API}/turno/partidos/${partido.id}`, { withCredentials: true });
      setPartido(response.data);
      setEventos(response.data.eventos || []);
      setResultado(response.data.resultado);
    } catch (error) {
      toast.error('Error al cargar detalle del partido');
    }
  };

  const handleRegistrarEvento = async (tipo, equipo = null) => {
    // Eventos que requieren equipo y descripción
    if (['gol', 'expulsion', 'penal_favor', 'penal_errado', 'incidencia_libre'].includes(tipo)) {
      setEventType(tipo);
      if (equipo) setEventTeam(equipo);
      
      // Pre-llenar descripción según tipo
      if (tipo === 'gol') {
        setDescripcion(equipo === 'local' ? `⚽ GOOOL ${partido.rival1_nombre_corto}` : `⚽ GOOOL ${partido.rival2_nombre_corto}`);
      } else if (tipo === 'expulsion') {
        setDescripcion(equipo === 'local' ? `🟥 Expulsión ${partido.rival1_nombre_corto}` : `🟥 Expulsión ${partido.rival2_nombre_corto}`);
      } else if (tipo === 'penal_favor') {
        setDescripcion(equipo === 'local' ? `Penal a favor de ${partido.rival1_nombre_corto}` : `Penal a favor de ${partido.rival2_nombre_corto}`);
      } else if (tipo === 'penal_errado') {
        setDescripcion(equipo === 'local' ? `Penal errado por ${partido.rival1_nombre_corto}` : `Penal errado por ${partido.rival2_nombre_corto}`);
      } else {
        setDescripcion('');
      }
      
      setShowEventDialog(true);
    } else {
      // Direct events (inicio_partido, termino_1er_tiempo, etc.)
      try {
        await axios.post(
          `${API}/turno/partidos/${partido.id}/eventos`,
          {
            tipo,
            descripcion: getEventDescription(tipo)
          },
          { withCredentials: true }
        );
        toast.success('Evento registrado');
        loadPartidoDetail();
      } catch (error) {
        toast.error('Error al registrar evento');
      }
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/turno/partidos/${partido.id}/eventos`,
        {
          tipo: eventType,
          equipo: eventTeam,
          descripcion
        },
        { withCredentials: true }
      );
      toast.success('Evento registrado');
      setShowEventDialog(false);
      setDescripcion('');
      loadPartidoDetail();
    } catch (error) {
      toast.error('Error al registrar evento');
    }
  };

  const handleFinalizar = async () => {
    try {
      await axios.put(
        `${API}/turno/partidos/${partido.id}/finalizar`,
        { comentario_final: comentarioFinal },
        { withCredentials: true }
      );
      toast.success('Partido finalizado');
      setShowFinalizarDialog(false);
      loadPartidoDetail();
    } catch (error) {
      toast.error('Error al finalizar partido');
    }
  };

  const getEventDescription = (tipo) => {
    const descriptions = {
      inicio_partido: 'Inicio del partido',
      termino_1er_tiempo: 'Término del primer tiempo',
      inicio_2do_tiempo: 'Inicio del segundo tiempo',
      termino_partido: 'Término del partido'
    };
    return descriptions[tipo] || tipo;
  };

  const getCurrentPhase = () => {
    const hasInicio = eventos.some(e => e.tipo === 'inicio_partido');
    const hasTermino1er = eventos.some(e => e.tipo === 'termino_1er_tiempo');
    const hasInicio2do = eventos.some(e => e.tipo === 'inicio_2do_tiempo');
    const hasTermino = eventos.some(e => e.tipo === 'termino_partido');

    if (hasTermino) return 'finalizado';
    if (hasInicio2do && !hasTermino) return 'segundo_tiempo';
    if (hasTermino1er && !hasInicio2do) return 'entretiempo';
    if (hasInicio && !hasTermino1er) return 'primer_tiempo';
    return 'no_iniciado';
  };

  const phase = getCurrentPhase();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-btn"
            onClick={onBack}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Match Info */}
        <Card className="p-6 mb-6 shadow-lg border-0" data-testid="match-info">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {partido.rival1_nombre_corto}
              <span className="mx-4 text-slate-400">vs</span>
              {partido.rival2_nombre_corto}
            </h2>
            <p className="text-slate-600">Hora: {partido.hora}</p>
          </div>

          {/* Score */}
          <div className="flex justify-center items-center gap-8 mb-6">
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-1">Local</p>
              <p className="text-5xl font-bold text-emerald-600" data-testid="score-local">{resultado?.goles_local || 0}</p>
              {resultado?.expulsiones_local > 0 && (
                <p className="text-sm text-red-600 mt-1">{resultado.expulsiones_local} expulsiones</p>
              )}
            </div>
            <div className="text-4xl font-bold text-slate-400">-</div>
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-1">Visita</p>
              <p className="text-5xl font-bold text-emerald-600" data-testid="score-visita">{resultado?.goles_visita || 0}</p>
              {resultado?.expulsiones_visita > 0 && (
                <p className="text-sm text-red-600 mt-1">{resultado.expulsiones_visita} expulsiones</p>
              )}
            </div>
          </div>

          {/* Controls */}
          {partido.estado !== 'finalizado' && (
            <div className="space-y-4">
              {/* Phase Controls */}
              <div className="grid grid-cols-2 gap-3">
                {phase === 'no_iniciado' && (
                  <Button
                    data-testid="inicio-partido-btn"
                    onClick={() => handleRegistrarEvento('inicio_partido')}
                    className="col-span-2 gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Play className="w-5 h-5" />
                    Iniciar Partido
                  </Button>
                )}
                {phase === 'primer_tiempo' && (
                  <>
                    <Button
                      data-testid="termino-1er-btn"
                      onClick={() => handleRegistrarEvento('termino_1er_tiempo')}
                      className="col-span-2 gap-2 bg-orange-600 hover:bg-orange-700"
                    >
                      <Clock className="w-5 h-5" />
                      Término 1er Tiempo
                    </Button>
                  </>
                )}
                {phase === 'entretiempo' && (
                  <Button
                    data-testid="inicio-2do-btn"
                    onClick={() => handleRegistrarEvento('inicio_2do_tiempo')}
                    className="col-span-2 gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Play className="w-5 h-5" />
                    Iniciar 2do Tiempo
                  </Button>
                )}
                {phase === 'segundo_tiempo' && (
                  <Button
                    data-testid="termino-partido-btn"
                    onClick={() => handleRegistrarEvento('termino_partido')}
                    className="col-span-2 gap-2 bg-red-600 hover:bg-red-700"
                  >
                    <Square className="w-5 h-5" />
                    Término Partido
                  </Button>
                )}
              </div>

              {/* Game Events */}
              {(phase === 'primer_tiempo' || phase === 'segundo_tiempo') && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      data-testid="gol-local-btn"
                      onClick={() => handleRegistrarEvento('gol', 'local')}
                      variant="outline"
                      className="gap-2 border-emerald-300 hover:bg-emerald-50"
                    >
                      <Target className="w-4 h-4" />
                      ⚽ Gol Local
                    </Button>
                    <Button
                      data-testid="gol-visita-btn"
                      onClick={() => handleRegistrarEvento('gol', 'visita')}
                      variant="outline"
                      className="gap-2 border-emerald-300 hover:bg-emerald-50"
                    >
                      <Target className="w-4 h-4" />
                      ⚽ Gol Visita
                    </Button>
                    <Button
                      data-testid="expulsion-local-btn"
                      onClick={() => handleRegistrarEvento('expulsion', 'local')}
                      variant="outline"
                      className="gap-2 border-red-300 hover:bg-red-50"
                    >
                      <UserX className="w-4 h-4" />
                      🟥 Expulsión Local
                    </Button>
                    <Button
                      data-testid="expulsion-visita-btn"
                      onClick={() => handleRegistrarEvento('expulsion', 'visita')}
                      variant="outline"
                      className="gap-2 border-red-300 hover:bg-red-50"
                    >
                      <UserX className="w-4 h-4" />
                      🟥 Expulsión Visita
                    </Button>
                  </div>

                  {/* Nuevos eventos */}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Button
                      onClick={() => handleRegistrarEvento('penal_favor', 'local')}
                      variant="outline"
                      className="gap-2 border-blue-300 hover:bg-blue-50"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Penal Local
                    </Button>
                    <Button
                      onClick={() => handleRegistrarEvento('penal_favor', 'visita')}
                      variant="outline"
                      className="gap-2 border-blue-300 hover:bg-blue-50"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Penal Visita
                    </Button>
                    <Button
                      onClick={() => handleRegistrarEvento('penal_errado', 'local')}
                      variant="outline"
                      className="gap-2 border-orange-300 hover:bg-orange-50"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Penal Errado Local
                    </Button>
                    <Button
                      onClick={() => handleRegistrarEvento('penal_errado', 'visita')}
                      variant="outline"
                      className="gap-2 border-orange-300 hover:bg-orange-50"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Penal Errado Visita
                    </Button>
                    <Button
                      onClick={() => handleRegistrarEvento('incidencia_libre')}
                      variant="outline"
                      className="col-span-2 gap-2 border-slate-300 hover:bg-slate-50"
                    >
                      <FileText className="w-4 h-4" />
                      Incidencia Libre
                    </Button>
                  </div>
                </>
              )}

              {/* Finalizar Partido Button */}
              {phase !== 'no_iniciado' && phase !== 'finalizado' && (
                <Button
                  data-testid="finalizar-partido-btn"
                  onClick={() => setShowFinalizarDialog(true)}
                  variant="destructive"
                  className="w-full"
                >
                  Finalizar Partido (en cualquier momento)
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Events Timeline */}
        <Card className="p-6 shadow-lg border-0" data-testid="events-timeline">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Eventos del Partido</h3>
          <div className="space-y-3">
            {eventos.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay eventos registrados</p>
            ) : (
              eventos.map((evento) => (
                <div
                  key={evento.id}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded"
                  data-testid="event-item"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{evento.descripcion}</p>
                    {evento.minuto != null && (
                      <p className="text-sm text-emerald-600 font-semibold">Minuto {evento.minuto}'</p>
                    )}
                    <p className="text-xs text-slate-500">
                      {new Date(evento.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          {resultado?.comentario_final && (
            <div className="mt-4 p-3 bg-emerald-50 rounded">
              <p className="text-sm font-semibold text-emerald-900">Comentario Final:</p>
              <p className="text-sm text-emerald-800">{resultado.comentario_final}</p>
            </div>
          )}
        </Card>
      </div>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>
              {eventType === 'gol' && '⚽ Registrar Gol'}
              {eventType === 'expulsion' && '🟥 Registrar Expulsión'}
              {eventType === 'penal_favor' && 'Registrar Penal a Favor'}
              {eventType === 'penal_errado' && 'Registrar Penal Errado'}
              {eventType === 'incidencia_libre' && 'Registrar Incidencia'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-4">
            <div>
              <Label>Comentario / Descripción</Label>
              <Textarea
                data-testid="event-desc-input"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Describe el evento..."
                rows={3}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                El minuto se calcula automáticamente
              </p>
            </div>
            <Button data-testid="event-save-btn" type="submit" className="w-full">
              Guardar Evento
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Finalizar Dialog */}
      <Dialog open={showFinalizarDialog} onOpenChange={setShowFinalizarDialog}>
        <DialogContent data-testid="finalizar-dialog">
          <DialogHeader>
            <DialogTitle>Finalizar Partido</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comentario Final (opcional)</Label>
              <Textarea
                data-testid="comentario-final-input"
                value={comentarioFinal}
                onChange={(e) => setComentarioFinal(e.target.value)}
                placeholder="Escribe un comentario sobre el partido..."
                rows={4}
              />
            </div>
            <Button
              data-testid="finalizar-confirm-btn"
              onClick={handleFinalizar}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              Finalizar Partido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
